import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveBlob } from './downloadFile';
import { useDarkMode } from './DarkModeContext';
import { LoadError, activateProps } from './ui';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

const STATUS_META = {
  received:     { label: 'Received',     bg: '#9ba3af', fg: '#fff' },
  under_review: { label: 'Under review', bg: '#c9a84c', fg: '#1a1a2e' },
  resolved:     { label: 'Resolved',     bg: '#2D4A3E', fg: '#fff' },
  closed:       { label: 'Closed',       bg: '#1a2744', fg: '#fff' },
};
const STATUS_ORDER = ['received', 'under_review', 'resolved', 'closed'];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.received;
  return (
    <span style={{ background: meta.bg, color: meta.fg, fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap' }}>
      {meta.label}
    </span>
  );
}

export default function GrievanceList() {
  const { isDarkMode } = useDarkMode();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // GRIEVFAILSAFE: this queue is whistleblowing reports. A failure reading as "No grievances
  // submitted." is not merely confusing here, it reads as an all-clear on something specifically
  // meant to surface concerns nobody else is watching for. This flag exists so a failure is never
  // representable as the empty state, at all - not "shown alongside it", not "usually visible
  // above it": genuinely a different branch that the empty-state copy cannot render under.
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState(null);

  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#888';
  const cardBg = isDarkMode ? '#1a2744' : '#fff';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.2)' : '1px solid #e8e4dc';

  const load = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await axios.get(`${API}/grievance/`);
      setRows(res.data || []);
    } catch {
      setRows([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/grievance/${id}/status`, { status });
      setRows(prev => prev.map(r => (r.id === id ? { ...r, status } : r)));
      setSelected(prev => (prev && prev.id === id ? { ...prev, status } : prev));
    } catch {
      /* keep UI as-is on failure */
    }
  };

  const fmtDate = (iso) => (iso ? toDisplayDate(iso).toLocaleDateString() : '-');
  const preview = (txt) => {
    if (!txt) return '-';
    return txt.length > 60 ? `${txt.slice(0, 60)}…` : txt;
  };

  const th = { textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: mutedCol, padding: '10px 12px', fontWeight: 600 };
  const td = { fontSize: '13px', color: textCol, padding: '11px 12px', borderTop: border };

  return (
    <div style={{ background: cardBg, border, borderRadius: '10px', padding: '22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: textCol, margin: 0 }}>Grievances</h3>
        <button onClick={load} style={{ fontSize: '12px', color: '#c9a84c', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Refresh</button>
      </div>

      {loading ? (
        <p style={{ color: mutedCol, fontSize: '14px' }}>Loading…</p>
      ) : loadFailed ? (
        // GRIEVFAILSAFE: checked BEFORE the empty-state branch below, so a failure can never fall
        // through to "No grievances submitted." - the wording says plainly that grievances may
        // exist and simply did not load, rather than anything a reader could take as an all-clear.
        <LoadError message="Grievances could not be loaded. There may be reports waiting that have not been shown." onRetry={load} />
      ) : rows.length === 0 ? (
        <p style={{ color: mutedCol, fontSize: '14px' }}>No grievances submitted.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Anonymous</th>
                <th style={th}>Concerning</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* KBDACCESS: row-click was the only way to open a grievance. The label names the
                  date and status only, never the concerning/description free text, so a screen
                  reader announcing the row cannot leak the content of a confidential report. */}
              {rows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(r)}
                  {...activateProps(() => setSelected(r), { label: `Open grievance submitted ${fmtDate(r.created_at)}, status ${(STATUS_META[r.status] || STATUS_META.received).label}` })}>
                  <td style={td}>{fmtDate(r.created_at)}</td>
                  <td style={td}>{r.is_anonymous ? 'Yes' : 'No'}</td>
                  <td style={td}>{preview(r.concerning)}</td>
                  <td style={td}><StatusBadge status={r.status} /></td>
                  <td style={td}>
                    <span style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 600 }}>View</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <GrievanceModal
          grievance={selected}
          isDarkMode={isDarkMode}
          onClose={() => setSelected(null)}
          onStatusChange={updateStatus}
        />
      )}
    </div>
  );
}

function GrievanceModal({ grievance: g, isDarkMode, onClose, onStatusChange }) {
  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#666';
  const panelBg = isDarkMode ? '#1a2744' : '#fff';

  const viewAttachment = async () => {
    try {
      const res = await axios.get(`${API}/grievance/${g.id}/attachment`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch {
      /* silently ignore - attachment may be missing or inaccessible */
    }
  };

  // DLNAME: the download counterpart. The name comes from the SERVER and is deliberately neutral
  // ("Grievance-YYYY-MM-DD.ext"): grievances are confidential, so no party's name, email or stored
  // filename may appear on a file sitting in someone's Downloads folder. The preview is unchanged.
  const downloadAttachment = async () => {
    try {
      const res = await axios.get(`${API}/grievance/${g.id}/attachment`, { responseType: 'blob' });
      saveBlob(res, 'Grievance');
    } catch {
      /* silently ignore - attachment may be missing or inaccessible */
    }
  };

  const field = (label, value) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: mutedCol, fontWeight: 600, marginBottom: '3px' }}>{label}</div>
      <div style={{ fontSize: '14px', color: textCol, whiteSpace: 'pre-wrap' }}>{value || '-'}</div>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,16,32,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', zIndex: 9000, overflowY: 'auto' }}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: panelBg, borderRadius: '12px', width: '100%', maxWidth: '640px', padding: '26px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: textCol, margin: '0 0 4px' }}>Grievance details</h3>
            <div style={{ fontSize: '12px', color: mutedCol }}>
              {g.is_anonymous ? 'Submitted anonymously' : (g.submitted_by_name || 'Unknown')}
              {' · '}{g.created_at ? toDisplayDate(g.created_at).toLocaleString() : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: mutedCol, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <span style={{ fontSize: '12px', color: mutedCol, fontWeight: 600 }}>Status</span>
          <select
            value={g.status}
            onChange={e => onStatusChange(g.id, e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cdd4de', background: isDarkMode ? '#243058' : '#fff', color: textCol, fontSize: '13px' }}
          >
            {STATUS_ORDER.map(s => (
              <option key={s} value={s}>{(STATUS_META[s] || {}).label || s}</option>
            ))}
          </select>
        </div>

        {field('Concerning', g.concerning)}
        {field('Investment project(s)', g.investment_project)}
        {field('Location', g.location)}
        {field('Who is affected', g.who_affected)}
        {field('Description', g.description)}
        {field('Actions already taken', g.actions_taken)}
        {field('Resolution requested', g.resolution_requested)}

        {g.attachment_url && (
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={viewAttachment}
              style={{ color: '#c9a84c', fontSize: '13px', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
            >
              View attachment{g.attachment_file_name ? ` (${g.attachment_file_name})` : ''}
            </button>
              {/* DLNAME: neutral, confidential file name from the server. */}
              <button onClick={downloadAttachment} style={{ background: 'none', border: 'none', color: '#c9a84c', fontSize: '13px', fontWeight: 600, padding: 0, cursor: 'pointer', textDecoration: 'underline', marginLeft: 8 }}>Download</button>
          </div>
        )}
      </div>
    </div>
  );
}
